import { createContext, useCallback, useContext, useRef, useState } from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { Flex } from '@radix-ui/themes';
import { CheckCircle, Warning, WarningCircle, X } from '@phosphor-icons/react';
import clsx from 'clsx';
import styles from './Toast.module.css';

type ToastVariant = 'success' | 'error' | 'warning';

type ToastMessage = {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
};

type ToastContextValue = {
  toast: (title: string, options?: { description?: string; variant?: ToastVariant }) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const ICON_CONTAINER_CLASS: Record<ToastVariant, string> = {
  success: styles.iconContainerSuccess,
  error: styles.iconContainerError,
  warning: styles.iconContainerWarning,
};

const ICON_CLASS: Record<ToastVariant, string> = {
  success: styles.iconSuccess,
  error: styles.iconError,
  warning: styles.iconWarning,
};

function VariantIcon({ variant }: { variant: ToastVariant }) {
  if (variant === 'success') {
    return <CheckCircle size={20} weight="fill" className={ICON_CLASS.success} />;
  }
  if (variant === 'error') {
    return <Warning size={20} weight="fill" className={ICON_CLASS.error} />;
  }
  return <WarningCircle size={20} weight="fill" className={ICON_CLASS.warning} />;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const counter = useRef(0);

  const toast = useCallback(
    (title: string, { description, variant = 'warning' }: { description?: string; variant?: ToastVariant } = {}) => {
      const id = ++counter.current;
      setMessages((prev) => [...prev, { id, title, description, variant }]);
    },
    [],
  );

  const dismiss = (id: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      <RadixToast.Provider swipeDirection="right" duration={5000}>
        {children}

        {messages.map((msg) => (
          <RadixToast.Root
            key={msg.id}
            open
            onOpenChange={(open: boolean) => {
              if (!open) {
                dismiss(msg.id);
              }
            }}
            className={styles.root}
          >
            <Flex align="center" gap="4">
              <Flex
                align="center"
                justify="center"
                width="40px"
                height="40px"
                className={clsx(styles.iconContainer, ICON_CONTAINER_CLASS[msg.variant])}
              >
                <VariantIcon variant={msg.variant} />
              </Flex>

              <Flex direction="column" gap="1" flexGrow="1">
                <RadixToast.Title className={styles.title}>
                  {msg.title}
                </RadixToast.Title>

                {msg.description && (
                  <RadixToast.Description className={styles.description}>
                    {msg.description}
                  </RadixToast.Description>
                )}
              </Flex>

              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => dismiss(msg.id)}
                aria-label="Dismiss notification"
              >
                <X size={20} className={styles.closeIcon} />
              </button>
            </Flex>
          </RadixToast.Root>
        ))}

        <RadixToast.Viewport className={styles.viewport} />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
